/**
 * THE PROJECTION. The one module that decides what an external model may see
 * of a stored Apiboo request, and the only place on this surface that reads a
 * field a user may have typed a credential into.
 *
 * ==========================================================================
 * THE RULE, STATED ONCE, AND IT IS AN ALLOWLIST
 * ==========================================================================
 *
 * A collections tool may emit, from a stored record, only:
 *
 *   (i)   field NAMES;
 *   (ii)  the values of fields whose NAME is on a fixed allowlist;
 *   (iii) substrings that are `{{template}}` references;
 *   (iv)  enumerations, counts, sizes, types and booleans derived from a value
 *         WITHOUT copying it.
 *
 * Everything else is `<hidden>`. **There is no branch here that copies a raw
 * value**, and no object is ever spread: every wire object below is built key
 * by key. `{...request}` is phase 2's `board_read` defect, and on this record
 * it would emit the url, every header value, the body and the whole auth block
 * in one character.
 *
 * ## Why this is an allowlist and not a denylist
 *
 * The owner's own framing was "non-Authorization headers". A denylist loses to
 * `X-API-Key`, `apikey`, `Cookie`, `X-Auth-Token`, `Proxy-Authorization`,
 * `brevo-api-key`, or whatever a vendor invents next month. So header NAMES are
 * always visible — they are what makes a request explainable — and header
 * VALUES are visible only for `SAFE_REQUEST_HEADER_VALUES` below. A name nobody
 * thought of is hidden by default rather than exposed by default, which is the
 * whole difference.
 *
 * ## Why showing `{{apiToken}}` unresolved is the heart of the design
 *
 * A stored request is a TEMPLATE by construction: substitution happens at send
 * time into fresh locals (`RequestBuilder.js:1413-1417, 1524-1533`) and neither
 * save payload (`:1751`, `:1770`) carries a resolved value. So
 * `Authorization: Bearer {{apiToken}}` in a record IS the whole record. Showing
 * it unresolved answers *"why does this request fail"* — the header exists, it
 * is wired to a variable, and `environments_list` says whether that variable is
 * set — while carrying no credential at all. The app says the same thing in its
 * own words (`utils/secretHeuristics.js:47-49`):
 *
 *     // A template reference is a pointer, not a secret — redacting it would
 *     // hide the wiring the model needs to explain the request.
 *
 * ## The four leaks a header rule does not cover
 *
 *   * **query strings** — `?api_key=…` bypasses every header rule, so the url's
 *     query is DISCARDED and REBUILT from projected params. An unprojected
 *     value cannot survive a rebuild.
 *   * **inline url credentials** — `https://user:pass@host` is dropped
 *     structurally, before anything else touches the url.
 *   * **request bodies** — a login payload and an OAuth token request carry
 *     credentials in the body. Only the key SKELETON with TYPED leaves is
 *     emitted; a leaf value is copied only when it is pointer-only.
 *   * **response bodies** — out entirely, in every tranche. There is no code
 *     path: the only headless last response lives on `store/tabsStore.js`,
 *     which `src/mcp/` may not even NAME (pinned by `mcpUiRefresh.test.js` and
 *     `mcpBoardUiRefresh.test.js`).
 *
 * ## What this deliberately does NOT use
 *
 * `secretHeuristics.looksSecret` / `redactSecretsInText` are best-effort by
 * their own words (`secretHeuristics.js:107-109`). They are the right tool
 * where free text is unavoidable and the WRONG tool where a field allowlist is
 * available — a probabilistic `<hidden>` is strictly worse than a structural
 * one. The single exception is `redactString` on the url PATH, which is the one
 * residue this contract cannot make structural: see `projectUrl`.
 */
import { redactString } from '../utils/redactResponse';
import { normalizeRequestBodyMode } from '../utils/requestBodyMode';
import { hasTemplateRef, isPointerOnly, templateRefNames } from './templateRefs';

/** What stands in for a value that may not be copied. */
export const HIDDEN = '<hidden>';

/**
 * Request headers whose VALUES are safe to show verbatim.
 *
 * Every name here is a protocol or content negotiation header: it describes the
 * shape of the exchange, never the identity of the caller. Adding a name is a
 * DECISION and `mcpRequestProjection.test.js` asserts this array exactly, in
 * order, so it cannot be widened by a drive-by.
 *
 * Deliberately absent even though they look harmless: `cookie`, `set-cookie`,
 * `authorization`, `proxy-authorization`, and EVERY `x-*` not named here. "The
 * header nobody thought of" is precisely the denylist failure this list exists
 * to avoid, and `x-*` is where that header always turns up.
 *
 * (`utils/redactResponse.js:17-24` has the RESPONSE-header twin. It is a
 * different list for a different direction and neither may be reused for the
 * other: `set-cookie` is safe to name on neither, and `retry-after` means
 * nothing on a request.)
 */
export const SAFE_REQUEST_HEADER_VALUES = Object.freeze([
  'accept',
  'accept-charset',
  'accept-encoding',
  'accept-language',
  'cache-control',
  'connection',
  'content-encoding',
  'content-length',
  'content-type',
  'expect',
  'from',
  'host',
  'if-match',
  'if-modified-since',
  'if-none-match',
  'if-unmodified-since',
  'max-forwards',
  'origin',
  'pragma',
  'range',
  'referer',
  'te',
  'trailer',
  'transfer-encoding',
  'upgrade-insecure-requests',
  'user-agent',
  'via',
  'x-requested-with',
]);

const SAFE_HEADER_SET = new Set(SAFE_REQUEST_HEADER_VALUES);

export function isSafeHeaderName(key) {
  return SAFE_HEADER_SET.has(String(key || '').trim().toLowerCase());
}

/**
 * ONE value gate, used by headers, params, cookies and form fields alike.
 *
 * `allowlisted` is the only way a literal value is ever copied, and the only
 * caller that passes `true` is the header projection for a name on the list
 * above. Everything else gets the pointer rule: a value made ONLY of
 * `{{references}}` (plus an optional auth scheme word and punctuation) is
 * shown, and anything carrying literal content of its own is hidden. Partial
 * disclosure is disclosure — `{{host}}-prod-9f8a7b` is hidden.
 */
function projectValue(rawValue, { allowlisted = false } = {}) {
  const value = typeof rawValue === 'string' ? rawValue : '';
  if (!value) return { value: '', hidden: false, refs: [] };
  const refs = templateRefNames(value);
  if (allowlisted) return { value, hidden: false, refs };
  if (isPointerOnly(value)) return { value, hidden: false, refs };
  return { value: HIDDEN, hidden: true, refs };
}

/**
 * `[{key, value, enabled}]` → `[{key, value, hidden, hiddenBecause, refs}]`.
 *
 * `kind` names why a value was hidden, so a model can tell "this is a secret
 * header" from "this is a secret query parameter" without guessing.
 */
export function projectKvRows(rows, kind) {
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => row && String(row.key || '').trim())
    .map((row) => {
      const key = String(row.key);
      const allowlisted = kind === 'header' && isSafeHeaderName(key);
      const projected = projectValue(row.value, { allowlisted });
      return {
        key,
        value: projected.value,
        ...(projected.hidden ? { hidden: true, hiddenBecause: `${kind}_value` } : { hidden: false }),
        enabled: row.enabled !== false,
        ...(projected.refs.length ? { variables: projected.refs } : {}),
      };
    });
}

/** `scheme://userinfo@host` — an `@` anywhere in the authority at all. */
const URL_USERINFO_RE = /^([a-z][a-z0-9+.-]*:\/\/)([^/?#\s]*@)/i;

/**
 * The url, taken apart and PUT BACK TOGETHER — never copied through.
 *
 * Three things happen in this order, and the order matters:
 *
 *   1. **userinfo is dropped structurally**, before anything else reads the
 *      string. `https://u:p@host/v1` becomes `https://host/v1` and
 *      `credentialsStripped: true` says so, because a silent strip would leave
 *      a model believing the stored request is fine.
 *   2. **the raw query is DISCARDED.** Not filtered — discarded. The query that
 *      comes back is rebuilt from the projected pairs, so a value the
 *      projection did not approve has no path into the result. `?api_key=LIVE`
 *      comes back as `?api_key=<hidden>` and `?page=2` survives.
 *   3. **the remaining base runs through `redactString`.**
 *
 * Step 3 is THE RESIDUE OF THIS WHOLE CONTRACT AND IT IS BEST-EFFORT — say so
 * out loud. A credential in a PATH SEGMENT (`https://api.x/v1/9f8a7b…/users`)
 * is not a header, not a query parameter and not a `{{template}}`, so no
 * structural rule can tell it from a tenant id. `redactString`
 * (`utils/redactResponse.js:53`) catches ≥32-char hex, ≥40-char base64, JWTs
 * and PEM blocks; it does not catch a short opaque key. This is the one place
 * the word "structural" is a near-claim, and it is named again in
 * `IMPLEMENTATION.md` rather than buried here.
 */
export function projectUrl(rawUrl, params) {
  const raw = typeof rawUrl === 'string' ? rawUrl.trim() : '';
  const projectedParams = projectKvRows(params, 'query');
  if (!raw) {
    return { url: '', credentialsStripped: false, queryRebuilt: false, params: projectedParams };
  }

  let credentialsStripped = false;
  const withoutUserinfo = raw.replace(URL_USERINFO_RE, (_m, scheme) => {
    credentialsStripped = true;
    return scheme;
  });

  const queryAt = withoutUserinfo.indexOf('?');
  const hashAt = withoutUserinfo.indexOf('#');
  const cut = [queryAt, hashAt].filter((i) => i >= 0);
  const base = cut.length ? withoutUserinfo.slice(0, Math.min(...cut)) : withoutUserinfo;
  const inlineQuery = queryAt >= 0 ? withoutUserinfo.slice(queryAt + 1, hashAt >= 0 ? hashAt : undefined) : '';

  // The pairs written into the url itself, projected by the SAME rules as the
  // `params` rows. They are merged into the reported parameter list so a model
  // sees one answer rather than two half-answers.
  const inlinePairs = projectKvRows(parseQueryPairs(inlineQuery), 'query');
  const allParams = [...inlinePairs, ...projectedParams];

  const rebuiltQuery = allParams
    .filter((p) => p.enabled)
    .map((p) => `${p.key}=${p.value}`)
    .join('&');

  const safeBase = redactString(base).value;
  return {
    url: rebuiltQuery ? `${safeBase}?${rebuiltQuery}` : safeBase,
    credentialsStripped,
    queryRebuilt: inlinePairs.length > 0,
    params: allParams,
    variables: templateRefNames(base),
  };
}

function parseQueryPairs(query) {
  if (!query) return [];
  return query
    .split('&')
    .filter(Boolean)
    .map((pair) => {
      const eq = pair.indexOf('=');
      const key = eq >= 0 ? pair.slice(0, eq) : pair;
      const value = eq >= 0 ? pair.slice(eq + 1) : '';
      return { key: safeDecode(key), value: safeDecode(value), enabled: true };
    })
    .filter((row) => row.key);
}

function safeDecode(text) {
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
}

/* ────────────────────────────── the body ────────────────────────────── */

const SHAPE_MAX_DEPTH = 8;
const SHAPE_MAX_KEYS = 200;

/**
 * A JSON value → its SHAPE: the same keys, with every leaf replaced by its
 * TYPE. `{"email":"a@b.c","password":"hunter2"}` becomes
 * `{"email":"<string>","password":"<string>"}` — the model learns the payload
 * the endpoint wants, which is what it needs to explain a 422, and learns
 * nothing about what is in it.
 *
 * The ONE exception is a pointer-only leaf: `{"token":"{{apiToken}}"}` comes
 * back verbatim, because a reference is wiring rather than content and it is
 * exactly the thing a diagnosis turns on.
 *
 * Keys are structure, not content — the same argument that makes a header NAME
 * always visible — so they are copied. Depth and key count are bounded so a
 * pathological document cannot turn a "shape" into a transcript.
 */
export function shapeOfJson(value, depth = 0, budget = { keys: 0 }) {
  if (depth > SHAPE_MAX_DEPTH) return '<deep>';
  if (value === null) return '<null>';
  if (Array.isArray(value)) {
    if (!value.length) return [];
    return [shapeOfJson(value[0], depth + 1, budget), `<${value.length} items>`];
  }
  if (typeof value === 'object') {
    const out = {};
    for (const [key, val] of Object.entries(value)) {
      budget.keys += 1;
      if (budget.keys > SHAPE_MAX_KEYS) {
        out['<truncated>'] = true;
        break;
      }
      out[key] = shapeOfJson(val, depth + 1, budget);
    }
    return out;
  }
  if (typeof value === 'string') {
    // A leaf that is only `{{references}}` is wiring, and wiring is the point.
    if (hasTemplateRef(value) && isPointerOnly(value)) return value;
    return '<string>';
  }
  if (typeof value === 'number') return '<number>';
  if (typeof value === 'boolean') return '<boolean>';
  return '<unknown>';
}

function byteLength(text) {
  try {
    return new TextEncoder().encode(typeof text === 'string' ? text : JSON.stringify(text ?? '')).length;
  } catch {
    return 0;
  }
}

/**
 * `{mode, contentType, bytes, shape}` — never body content.
 *
 * Per mode:
 *   * **raw + json** — the key skeleton above. Unparseable JSON reports
 *     `shape: null` and `parsed: false` rather than falling back to text: the
 *     app's own standing decision is that a raw/unparseable body is not
 *     redacted, and the way to respect that here is to not return one at all.
 *   * **raw + text/xml** — `bytes` only. XML has no cheap safe skeleton and
 *     text has none at all.
 *   * **form-data / urlencoded** — field NAMES, with `hasValue` and
 *     `isPointer` per field, and the pointer's variables. Names are the schema;
 *     values are the payload.
 *   * **graphql** — the operation name, the declared `$variable` NAMES and the
 *     top-level field names, extracted as IDENTIFIERS ONLY. String literals in
 *     the query text are never entered, so a `login(password: "hunter2")`
 *     argument cannot escape through a field name.
 */
export function projectBody(request) {
  const { bodyType, rawBodyLanguage } = normalizeRequestBodyMode(request || {});
  const contentType = contentTypeOf(request);

  if (bodyType === 'none') return { mode: 'none', contentType, bytes: 0, shape: null };

  if (bodyType === 'form-data' || bodyType === 'urlencoded') {
    const fields = (Array.isArray(request?.formDataFields) ? request.formDataFields : [])
      .filter((f) => f && String(f.key || '').trim())
      .map((f) => {
        const value = typeof f.value === 'string' ? f.value : '';
        return {
          key: String(f.key),
          type: f.type === 'file' ? 'file' : 'text',
          hasValue: value.length > 0,
          isPointer: value.length > 0 && isPointerOnly(value),
          ...(templateRefNames(value).length ? { variables: templateRefNames(value) } : {}),
          enabled: f.enabled !== false,
        };
      });
    return { mode: bodyType, contentType, bytes: byteLength(fields.map((f) => f.key).join('&')), fields, shape: null };
  }

  if (bodyType === 'graphql') {
    return { mode: 'graphql', contentType, ...projectGraphql(request?.body) };
  }

  const raw = typeof request?.body === 'string' ? request.body : '';
  const bytes = byteLength(raw);
  if (rawBodyLanguage !== 'json') {
    return { mode: 'raw', language: rawBodyLanguage, contentType, bytes, shape: null };
  }
  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { mode: 'raw', language: 'json', contentType, bytes, parsed: false, shape: null };
  }
  return {
    mode: 'raw',
    language: 'json',
    contentType,
    bytes,
    parsed: true,
    shape: shapeOfJson(parsed),
  };
}

/** The Content-Type the request declares, if any. It is on the allowlist. */
export function contentTypeOf(request) {
  const rows = Array.isArray(request?.headers) ? request.headers : [];
  const row = rows.find(
    (h) => h && h.enabled !== false && String(h.key || '').trim().toLowerCase() === 'content-type'
  );
  return row ? String(row.value || '') : null;
}

/** Identifiers only — a string literal in the query text is never entered. */
function projectGraphql(body) {
  const gql = body && typeof body === 'object' && !Array.isArray(body) ? body : {};
  const query = typeof gql.query === 'string' ? gql.query : '';
  const variablesText = typeof gql.variables === 'string' ? gql.variables : '';
  const declared = [];
  const varRe = /\$([A-Za-z_][A-Za-z0-9_]*)\s*:/g;
  let m = varRe.exec(query);
  while (m) {
    if (!declared.includes(m[1])) declared.push(m[1]);
    m = varRe.exec(query);
  }
  return {
    operationName: typeof gql.operationName === 'string' && gql.operationName ? gql.operationName : null,
    operationType: /^\s*(query|mutation|subscription)\b/i.exec(query)?.[1]?.toLowerCase() || 'query',
    fields: topLevelGraphqlFields(query),
    declaredVariables: declared,
    hasVariables: variablesText.trim().length > 0,
    bytes: byteLength(query) + byteLength(variablesText),
    shape: null,
  };
}

/**
 * The field names at the first selection level. A hand walk rather than a
 * parser, because the ONLY thing that may leave this function is an identifier
 * `[A-Za-z_][A-Za-z0-9_]*`: quoted string literals are skipped whole and
 * argument lists are skipped whole, so no user content can be mistaken for a
 * field name.
 */
function topLevelGraphqlFields(query) {
  const out = [];
  let depth = 0;
  let i = 0;
  while (i < query.length && out.length < 50) {
    const ch = query[i];
    if (ch === '"') {
      i += 1;
      while (i < query.length && query[i] !== '"') i += query[i] === '\\' ? 2 : 1;
      i += 1;
      continue;
    }
    if (ch === '(') {
      let parens = 1;
      i += 1;
      while (i < query.length && parens > 0) {
        if (query[i] === '"') {
          i += 1;
          while (i < query.length && query[i] !== '"') i += query[i] === '\\' ? 2 : 1;
        } else if (query[i] === '(') parens += 1;
        else if (query[i] === ')') parens -= 1;
        i += 1;
      }
      continue;
    }
    if (ch === '{') {
      depth += 1;
      i += 1;
      continue;
    }
    if (ch === '}') {
      depth -= 1;
      i += 1;
      continue;
    }
    if (depth === 1 && /[A-Za-z_]/.test(ch)) {
      let j = i;
      while (j < query.length && /[A-Za-z0-9_]/.test(query[j])) j += 1;
      const word = query.slice(i, j);
      if (!out.includes(word)) out.push(word);
      i = j;
      continue;
    }
    i += 1;
  }
  return out;
}

/* ────────────────────────────── the auth block ────────────────────────── */

/**
 * Every auth field that carries a credential, by type. A field NOT listed —
 * an api key's `key`, a basic auth's `username`, the `in` selector, an OAuth2
 * `grantType`, `authUrl`, `tokenUrl`, `scope` — is a name, a position or a
 * public endpoint, and is reported as SET or not rather than hidden.
 *
 * `oauth2` and `awssigv4` are here in full even though **O32** refuses to WRITE
 * them: an existing request the USER made in the app can carry either, and the
 * read side must project what exists rather than what MCP can create.
 */
const AUTH_CREDENTIAL_FIELDS = Object.freeze({
  bearer: ['bearer'],
  apikey: ['value'],
  basic: ['password'],
  oauth2: ['clientSecret', 'accessToken', 'refreshToken', 'password'],
  awssigv4: ['secretAccessKey', 'sessionToken', 'accessKeyId'],
});

/**
 * `{type, fieldsSet, wiredTo, literalFields}` and nothing else.
 *
 * The shape follows `utils/shareRedaction.js`'s `redactAuth` (`:164-174`),
 * which collapses an auth block to its `type` — copied as a SHAPE, not as a
 * call, because importing it would drag `utils/urlParams` and
 * `secretHeuristics` into this graph for two lines. Widened with `wiredTo`,
 * which is the whole diagnostic value: *"this request's bearer token comes from
 * `{{apiToken}}`, and `apiToken` is not set in the selected environment"* is the
 * answer to the owner's question, and it is one variable NAME away from being
 * unanswerable.
 *
 * `literalFields` names the fields that hold a literal without saying what it
 * is. That is a HYGIENE signal, deliberately: it lets an agent tell the user
 * "this request has a token pasted into it, move it to a variable" — which is
 * the advice the app itself gives (`toolSchemas.js:683`).
 */
export function projectAuth(auth) {
  const type = auth && typeof auth === 'object' ? String(auth.type || 'inherit') : 'inherit';
  const fieldsSet = [];
  const wiredTo = [];
  const literalFields = [];

  const visit = (prefix, obj) => {
    if (!obj || typeof obj !== 'object') return;
    for (const [key, value] of Object.entries(obj)) {
      if (key === 'type') continue;
      const path = prefix ? `${prefix}.${key}` : key;
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        visit(path, value);
        continue;
      }
      if (typeof value !== 'string' || !value) continue;
      fieldsSet.push(path);
      for (const name of templateRefNames(value)) {
        if (!wiredTo.includes(name)) wiredTo.push(name);
      }
      if (isCredentialField(path) && !isPointerOnly(value)) literalFields.push(path);
    }
  };
  visit('', auth);

  return { type, fieldsSet: fieldsSet.sort(), wiredTo, literalFields: literalFields.sort() };
}

function isCredentialField(path) {
  const [head, tail] = path.includes('.') ? path.split('.') : [null, path];
  if (head && AUTH_CREDENTIAL_FIELDS[head]) return AUTH_CREDENTIAL_FIELDS[head].includes(tail);
  // A top-level credential: the HTTP builder's `bearer`, or the SSE builder's
  // own `token` (`SSEBuilder.js` stores `{type: 'bearer', token}`).
  return TOP_LEVEL_CREDENTIAL_FIELDS.includes(path);
}

const TOP_LEVEL_CREDENTIAL_FIELDS = Object.freeze(['bearer', 'token']);

/* ─────────────────────────────── scripts ─────────────────────────────── */

/**
 * **O33.** Scripts are neither readable nor writable over MCP. What comes back
 * is that one EXISTS, how long it is, and which variables it sets — enough for
 * a model to say "a pre-request script sets `signature`, so that header is not
 * as unset as it looks", and not enough to read a pasted literal out of the
 * field most likely to hold one.
 */
export function projectScript(source) {
  const text = typeof source === 'string' ? source : '';
  if (!text.trim()) return { present: false, lines: 0, setsVariables: [] };
  const sets = [];
  const re = /pm\.(?:environment|globals|variables|collectionVariables)\.set\(\s*['"`]([^'"`]+)['"`]/g;
  let m = re.exec(text);
  while (m) {
    if (!sets.includes(m[1])) sets.push(m[1]);
    m = re.exec(text);
  }
  return { present: true, lines: text.split('\n').length, setsVariables: sets };
}

/* ───────────────────────────── mock examples ─────────────────────────── */

/**
 * A mock example's body is content the USER (or an agent) wrote as a fake
 * response — and "fake" is a claim nobody verified. Real captured payloads end
 * up in there. So the same rule applies: counts, status codes, sizes, and the
 * allowlisted headers. Never the body.
 */
export function projectMockExamples(request) {
  const examples = Array.isArray(request?.mock?.responseExamples) ? request.mock.responseExamples : [];
  return examples.map((ex) => ({
    id: String(ex?.id ?? ''),
    name: String(ex?.name || ''),
    status: Number(ex?.status) || 0,
    contentType: String(ex?.contentType || ''),
    bytes: byteLength(ex?.body ?? ''),
    enabled: ex?.enabled !== false,
    sourceType: ex?.sourceType === 'schema' ? 'schema' : 'example',
    matchQuery: projectKvRows(ex?.matchQuery, 'query').map((r) => r.key),
    matchHeaders: projectKvRows(ex?.matchHeaders, 'header').map((r) => r.key),
  }));
}

/**
 * The `{{references}}` a projected body SHAPE carries. Only pointer-only leaves
 * survive into a shape, so this walks what is already safe to emit — it never
 * touches the raw body.
 */
function bodyShapeRefs(shape, out = []) {
  if (typeof shape === 'string') {
    for (const name of templateRefNames(shape)) if (!out.includes(name)) out.push(name);
    return out;
  }
  if (Array.isArray(shape)) {
    for (const item of shape) bodyShapeRefs(item, out);
    return out;
  }
  if (shape && typeof shape === 'object') {
    for (const value of Object.values(shape)) bodyShapeRefs(value, out);
  }
  return out;
}

/* ─────────────────────────── the whole request ───────────────────────── */

/**
 * The wire object for one request. **Built key by key. Never spread.**
 *
 * A field added to the record model tomorrow appears here only if somebody
 * writes a line for it — and if they write a copy-the-value line,
 * `mcpRequestProjection.test.js`'s sentinel sweep goes red. That is what makes
 * the contract structural over an OPEN record shape rather than a rule someone
 * has to remember.
 */
export function projectRequest(request, { collectionId, collectionName } = {}) {
  const url = projectUrl(request?.url, request?.params);
  const auth = projectAuth(request?.auth);
  const body = projectBody(request);
  const headers = projectKvRows(request?.headers, 'header');
  const cookies = projectKvRows(request?.cookies, 'cookie');

  const variables = [];
  for (const name of [
    ...(url.variables || []),
    ...headers.flatMap((h) => h.variables || []),
    ...url.params.flatMap((p) => p.variables || []),
    ...auth.wiredTo,
    ...(body.fields || []).flatMap((f) => f.variables || []),
    // NOT `body.declaredVariables`: a GraphQL `$id` is an operation variable
    // belonging to the query document, not an Apiboo `{{variable}}`, and
    // mixing the two would send a diagnosis hunting for a variable scope that
    // was never meant to hold it.
    ...bodyShapeRefs(body.shape),
  ]) {
    if (!variables.includes(name)) variables.push(name);
  }

  return {
    id: String(request?.id ?? ''),
    name: String(request?.name || ''),
    method: String(request?.method || 'GET').toUpperCase(),
    url: url.url,
    urlCredentialsStripped: url.credentialsStripped,
    ...(collectionId ? { collectionId: String(collectionId) } : {}),
    ...(collectionName ? { collectionName: String(collectionName) } : {}),
    folderId: request?.folder_id == null ? null : String(request.folder_id),
    // The user's own prose, verbatim, and this is the whole point of the ask:
    // "structure it nice also write documentation". It is also the residue the
    // report names — a credential someone typed into a description is text we
    // return, exactly as it is for documents (O5).
    description: typeof request?.description === 'string' ? request.description : '',
    headers,
    params: url.params,
    cookies,
    body,
    auth,
    preRequestScript: projectScript(request?.preRequestScript),
    testScript: projectScript(request?.testScript),
    mockExamples: projectMockExamples(request),
    variables,
    type: String(request?.type || 'http'),
    sortOrder: typeof request?.sort_order === 'number' ? request.sort_order : 0,
  };
}
